import { TRAFFIC_DEVICE_LABELS, TRAFFIC_SOURCE_LABELS, type TrafficStats } from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardHeader, Skeleton } from "../../components/ui/card";
import { orpc } from "../../lib/api";
import { cn } from "../../lib/utils";
import { AdminPageHeader } from "./layout";

/** Landing page statistics (docs/18-landingpage.md): counters only, no personal data. */

const RANGES = [
  { days: 7, label: "7 Tage" },
  { days: 30, label: "30 Tage" },
  { days: 90, label: "90 Tage" },
  { days: 365, label: "Jahr" },
];

const number = new Intl.NumberFormat("de-DE");

function shortDay(day: string) {
  return new Date(`${day}T12:00:00`).toLocaleDateString("de-DE", {
    day: "numeric",
    month: "short",
  });
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-4">
      <p className="text-sm text-zinc-500">{label}</p>
      <p className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">
        {number.format(value)}
      </p>
    </Card>
  );
}

/** Bars per day; the tooltip of each bar gives the exact numbers. */
function DayChart({ data }: { data: TrafficStats["byDay"] }) {
  const max = Math.max(1, ...data.map((row) => row.views));
  return (
    <div>
      <div className="flex h-40 items-end gap-px" role="img" aria-label="Aufrufe pro Tag">
        {data.map((row) => (
          <div
            key={row.day}
            title={`${shortDay(row.day)}: ${number.format(row.views)} Aufrufe, ${number.format(row.visitors)} Besucher`}
            className="group relative flex h-full min-w-px flex-1 items-end"
          >
            <div
              className="w-full rounded-t-sm bg-brand-600 group-hover:bg-brand-700"
              style={{ height: `${(row.views / max) * 100}%`, minHeight: row.views ? 2 : 0 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-zinc-400">
        <span>{shortDay(data[0]?.day ?? "")}</span>
        <span>{shortDay(data[data.length - 1]?.day ?? "")}</span>
      </div>
    </div>
  );
}

function BarList({
  rows,
  unit,
}: {
  rows: { label: string; value: number; extra?: string }[];
  unit: string;
}) {
  const max = Math.max(1, ...rows.map((row) => row.value));
  if (rows.length === 0) return <p className="px-5 py-6 text-sm text-zinc-400">Noch keine Daten</p>;
  return (
    <ul className="space-y-2.5 p-5">
      {rows.map((row) => (
        <li key={row.label} className="text-sm">
          <div className="flex justify-between gap-3">
            <span className="min-w-0 truncate text-zinc-800">{row.label}</span>
            <span className="shrink-0 text-zinc-500 tabular-nums">
              {number.format(row.value)} {unit}
              {row.extra ? ` · ${row.extra}` : ""}
            </span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-zinc-100">
            <div
              className="h-full rounded-full bg-brand-600"
              style={{ width: `${(row.value / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function AdminTrafficPage() {
  const [days, setDays] = useState(30);
  const query = useQuery({
    ...orpc.admin.traffic.queryOptions({ input: { days } }),
    refetchInterval: 60_000,
  });
  const data = query.data;

  return (
    <>
      <AdminPageHeader
        title="Besucher"
        description="Aufrufe der Landingpage. Ohne Cookies und ohne personenbezogene Daten gezählt."
        actions={
          <div className="flex rounded-lg border border-zinc-200 bg-white p-0.5">
            {RANGES.map((range) => (
              <button
                key={range.days}
                type="button"
                onClick={() => setDays(range.days)}
                className={cn(
                  "rounded-md px-3 py-1 text-sm font-medium",
                  days === range.days
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-600 hover:bg-zinc-100",
                )}
              >
                {range.label}
              </button>
            ))}
          </div>
        }
      />
      {!data ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Seitenaufrufe" value={data.totals.views} />
            <Stat label="Besucher (pro Tag gezählt)" value={data.totals.visitors} />
            <Stat
              label="Aufrufe pro Tag"
              value={Math.round(data.totals.views / Math.max(1, data.days))}
            />
          </div>
          <Card className="p-5">
            <DayChart data={data.byDay} />
          </Card>
          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader title="Seiten" description="Aufrufe" />
              <BarList
                unit="Aufrufe"
                rows={data.byPage.map((row) => ({ label: row.path, value: row.views }))}
              />
            </Card>
            <Card>
              <CardHeader
                title="Herkunft"
                description="Woher Besucher kamen (Verweis-Seite oder utm_source)"
              />
              <BarList
                unit="Besucher"
                rows={data.bySource.map((row) => ({
                  label: TRAFFIC_SOURCE_LABELS[row.source] ?? row.source,
                  value: row.visitors,
                }))}
              />
            </Card>
            <Card>
              <CardHeader title="Geräte" />
              <BarList
                unit="Aufrufe"
                rows={data.byDevice.map((row) => ({
                  label: TRAFFIC_DEVICE_LABELS[row.device],
                  value: row.views,
                  extra: `${number.format(row.visitors)} Besucher`,
                }))}
              />
            </Card>
            <Card>
              <CardHeader title="Browser" />
              <BarList
                unit="Aufrufe"
                rows={data.byBrowser.map((row) => ({
                  label: row.browser,
                  value: row.views,
                  extra: `${number.format(row.visitors)} Besucher`,
                }))}
              />
            </Card>
          </div>
          <p className="text-xs text-zinc-500">
            Ein Besucher wird pro Tag einmal gezählt und nicht über Tage hinweg wiedererkannt.
            Anfragen mit „Do Not Track“, Bots und Adblocker fehlen in den Zahlen.
          </p>
        </div>
      )}
    </>
  );
}
